import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../Components/Cta";
import {
  CustomFileUpload,
  CustomTextArea,
  CustomSearchInput,
  CustomSelectMenu,
  Input,
  RadioGroup,
  ToggleInput,
} from "../../Components/FormElements";
import { APIService } from "../../Service";
import Loader from "../../Components/Other/loader";
import { CustomSelectBox } from "../../Components/DropDown";
import { toast } from "react-toastify";

export default function AddBasket() {
  const navigate = useNavigate();

  // --- Form states (refactored to separate name & description) ---
  const [basketName, setBasketName] = useState("");
  const [basketDescription, setBasketDescription] = useState("");
  const [basketDescriptionError, setBasketDescriptionError] = useState(false);
  const [order, setOrder] = useState("");
  const [orderError, setOrderError] = useState(false);
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [linkError, setLinkError] = useState(false); // basketName error
  const [answer, setAnswer] = useState("");
  const [answerError, setAnswerError] = useState(false);

  // features: always keep as an array of strings and parallel array of booleans for errors
  const [featureFields, setFeatureFields] = useState([""]);
  const [featureErrors, setFeatureErrors] = useState([false]); // per-feature errors

  const [estimatedReturns, setEstimatedReturns] = useState("");
  const [estimatedReturnsError, setEstimatedReturnsError] = useState(false);
  const [minSip, setMinSip] = useState("");
  const [minSipError, setMinSipError] = useState(false);
  const [lumpsum, setLumpSum] = useState("");
  const [lumpSumError, setLumpSumError] = useState(false);
  const [basketTag, setBasketTag] = useState(false);
  const [showOnHomePage, setShowOnHomePage] = useState(false);
  const [selectedOptionGrowth, setSelectedOptionGrowth] = useState();
  const [option, setOption] = useState([]);
  const [returnYear, setReturnYear] = useState("3Y");
  const [selectedOptionGrowthError, setSelectedOptionGrowthError] =
    useState(false);

  // header/meta
  const [loading, setLoading] = useState(false);
  const [btnloading, setBtnLoading] = useState(false);

  // Fund Search
  const [searchValue, setSearchValue] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);

  // Funds shown in the "Total Funds" list (will be populated from API)
  const [fundsData, setFundsData] = useState([]);
  const [fundsAllocationError, setFundsAllocationError] = useState(false);
  const [searchedFunds, setSearchedFunds] = useState([]);
  const [allocationSum, setAllocationSum] = useState(0);

  const [suitableFor, setSuitableFor] = useState("");
  const [suitableForError, setSuitableForError] = useState(false);

  const [riskFilterData, setRiskFilterData] = useState([]);
  const [curRisk, setCurRisk] = useState("");
  const [riskError, setRiskError] = useState("");

  const [timeHorizon, setTimeHorizon] = useState("");
  const [timeHorizonError, setTimeHorizonError] = useState(false);
  const [timeHorizonData, setTimeHorizonData] = useState([]);

  // file error (optional — kept for completeness)
  const [fileError, setFileError] = useState(false);

  // Keep featureErrors in sync with featureFields length
  useEffect(() => {
    setFeatureErrors((prev) => {
      const next = featureFields.map((f, idx) => {
        if (Array.isArray(prev) && typeof prev[idx] === "boolean")
          return prev[idx];
        return false;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFields.length]);

  useEffect(() => {
    _getFundsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    _getFilterData();
    _getRiskFilterData();
    _getTimeHorizonData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute allocation sum whenever fundsData changes
  useEffect(() => {
    if (!fundsData || fundsData.length === 0) {
      setAllocationSum(0);
      setFundsAllocationError(false);
      return;
    }

    const sum = fundsData.reduce((acc, f) => {
      const v =
        f.allocation_percentage === "" || f.allocation_percentage === null
          ? 0
          : parseFloat(f.allocation_percentage) || 0;
      return acc + v;
    }, 0);

    // guard for floating point rounding
    const rounded = Number(sum.toFixed(3));
    setAllocationSum(rounded);

    const isExactly100 = Math.abs(rounded - 100) <= 0.001;

    if (!isExactly100) {
      setFundsAllocationError("Allocation total should be 100% (sum to 100)");
    } else {
      setFundsAllocationError(false);
    }
  }, [fundsData]);

  const _addBanner = (e) => {
    if (e.key === "Enter") {
      if (!basketName || !basketName.trim()) {
        setLinkError("Enter Basket Name");
      } else {
        setLinkError(false);
      }
    }
  };

  const _handleSave = () => {
    // reset errors
    let hasError = false;
    setLinkError(false);
    setAnswerError(false);
    setMinSipError(false);
    setLumpSumError(false);
    setEstimatedReturnsError(false);
    setFeatureErrors(featureFields.map(() => false));
    setSelectedOptionGrowthError(false);
    setFundsAllocationError(false);
    setBasketDescriptionError(false);
    setOrderError(false);
    setFileError(false);
    setSuitableForError(false);
    setRiskError(false);
    setTimeHorizonError(false);

    // basket name required
    if (!basketName || !basketName.trim()) {
      setLinkError("Enter Basket name");
      hasError = true;
    }
    // About basket required
    if (!answer || !answer.trim()) {
      setAnswerError("Enter About section");
      hasError = true;
    }
    // risk value required
    if (!curRisk.value) {
      setRiskError("Risk value required");
      hasError = true;
    }
    // time horizon required
    if (!timeHorizon || !timeHorizon.value) {
      setTimeHorizonError("Select Time Horizon");
      hasError = true;
    }
    // suitable for required
    if (!suitableFor || !suitableFor.trim()) {
      setSuitableForError("Enter Suitable For");
      hasError = true;
    }
    // basket description required
    if (!basketDescription || !basketDescription.trim()) {
      setBasketDescriptionError("Add a description");
      hasError = true;
    }

    // Order required and numeric > 0
    const orderNum = parseFloat(String(order).replace(/[^0-9.-]+/g, ""));
    if (!order || isNaN(orderNum) || orderNum <= 0) {
      setOrderError("Enter Order");
      hasError = true;
    }

    // Features: ensure at least one feature and no empty strings
    if (!featureFields || featureFields.length === 0) {
      setFeatureErrors([true]);
      hasError = true;
    } else {
      const errs = featureFields.map((f) => {
        return !f || !String(f).trim();
      });
      setFeatureErrors(errs);
      if (errs.some((e) => e === true)) hasError = true;
    }

    // Min SIP required and numeric > 0
    const minSipNum = parseFloat(String(minSip).replace(/[^0-9.-]+/g, ""));
    if (!minSip || isNaN(minSipNum) || minSipNum <= 0) {
      setMinSipError("Enter Min SIP");
      hasError = true;
    }

    // Min Lumpsum required and numeric > 0
    const lumpNum = parseFloat(String(lumpsum).replace(/[^0-9.-]+/g, ""));
    if (!lumpsum || isNaN(lumpNum) || lumpNum <= 0) {
      setLumpSumError("Enter Min Lumpsum");
      hasError = true;
    }

    // Past Returns required and numeric (can be 0)
    const retNum = parseFloat(String(estimatedReturns).replace("%", ""));
    if (estimatedReturns === "" || isNaN(retNum)) {
      setEstimatedReturnsError("Enter Estimated Returns");
      hasError = true;
    }

    // If recommend tag toggle is on, tag selection is required
    if (basketTag && !selectedOptionGrowth) {
      setSelectedOptionGrowthError("Select One Option");
      hasError = true;
    }

    // Funds allocation must sum to 100%
    if (Math.abs(allocationSum - 100) > 0.001) {
      setFundsAllocationError("Funds allocation must sum to 100%");
      hasError = true;
    }

    // check file
    if (!fileUrl) {
      setFileError("Image Field is Required");
      hasError = true;
    }

    if (!hasError) {
      setLoading(true);
      _addBakset();
    }
  };

  const handleAddNew = () => {
    if (featureFields.length < 3) {
      setFeatureFields((prev) => [...prev, ""]);
      setFeatureErrors((prev) =>
        Array.isArray(prev) ? [...prev, false] : [false],
      );
    }
  };

  const convertedFunds = fundsData.map((fund) => ({
    fund_id: Number(fund.fund_id),
    allocation_id: fund.allocation_id ?? null,
    allocation_percentage: Number(fund.allocation_percentage) || 0,
  }));

  const handleChange = (index, value) => {
    const updatedFields = [...featureFields];
    updatedFields[index] = value;
    setFeatureFields(updatedFields);

    const updatedErrors = [
      ...(Array.isArray(featureErrors) ? featureErrors : []),
    ];
    updatedErrors[index] = !value || !String(value).trim();
    setFeatureErrors(updatedErrors);
  };

  const handleRemove = (indexToRemove) => {
    const updatedFields = featureFields.filter(
      (_, index) => index !== indexToRemove,
    );
    setFeatureFields(updatedFields);

    setFeatureErrors((prev) => {
      if (!Array.isArray(prev)) return [];
      return prev.filter((_, index) => index !== indexToRemove);
    });
  };

  const handleAddFunds = () => {
    setFundsAllocationError(false);
    setFundsData((prev) => {
      // normalize existing IDs to strings to avoid number/string mismatches
      const existingIds = new Set(prev.map((f) => String(f.fund_id)));

      // dedupe selectedOptions (keep first occurrence)
      const seen = new Set();
      const dedupedSelected = [];
      for (const s of selectedOptions) {
        const id = String(s.fund_id);
        if (!seen.has(id)) {
          dedupedSelected.push(s);
          seen.add(id);
        }
      }

      // keep only those that are not already in fundsData
      const toAdd = dedupedSelected.filter(
        (s) => !existingIds.has(String(s.fund_id)),
      );

      if (toAdd.length === 0) {
        setFundsAllocationError("Fund Already present");
        return prev;
      }

      const newOptions = toAdd.map((elem) => ({
        ...elem,
        allocation_percentage: 0,
        allocation_id: null,
      }));

      return [...prev, ...newOptions];
    });

    setSearchValue("");
  };

  const handleAllocationChange = (fundId, rawValue) => {
    // rawValue comes from e.target.value (string)
    // allow empty string while typing, treat as 0 in sum
    let value = rawValue === "" ? "" : Number(rawValue);

    // invalid numeric entry -> ignore
    if (value !== "" && Number.isNaN(value)) return;

    // clamp to 0..100 when numeric
    if (value !== "" && value < 0) value = 0;
    if (value !== "" && value > 100) value = 100;

    setFundsData((prev) =>
      prev.map((f) =>
        f.fund_id === fundId ? { ...f, allocation_percentage: value } : f,
      ),
    );
  };
  const handleDeleteFund = (fundId) => {
    setFundsAllocationError(false);
    setFundsData((prev) => prev.filter((f) => f.fund_id !== fundId));
  };

  function _handleImageUpload(selectedFile) {
    setFileError(false);
    setFile(selectedFile);
  }

  //service to get funds details
  const _getFundsList = () => {
    const is_post = true;
    const payload = {
      query: searchValue,
    };
    APIService(is_post, "manage-basket/get-fund", payload)
      .then((response) => {
        if (response.status_code === 200) {
          setSearchedFunds(response.data.funds);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };
  //service to get filter data
  const _getFilterData = () => {
    const is_post = false;
    APIService(is_post, "manage-basket/filter-tag")
      .then((response) => {
        if (response.status_code === 200) {
          setOption(response.data);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  //service to get time horizon filter data
  const _getTimeHorizonData = () => {
    APIService(false, "manage-basket/time-horizon-filter")
      .then((response) => {
        if (response.status_code === 200) {
          setTimeHorizonData(response.data.time_horizon);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  //service to get risk filter data
  const _getRiskFilterData = () => {
    APIService(false, "manage-basket/filter-data")
      .then((response) => {
        if (response.status_code === 200) {
          const filterData = response.data?.investment_style?.map((e) => ({
            label: e.label,
            value: e.label,
          }));
          setRiskFilterData(filterData);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  //service to calculate Returns
  const _calculateReturns = () => {
    setBtnLoading(true);
    const is_post = true;
    const payload = {
      funds: fundsData?.map((e) => {
        return { fund_id: e.fund_id, allocation: e.allocation_percentage };
      }),
    };
    APIService(is_post, "manage-basket/returns", payload)
      .then((response) => {
        if (response.status_code === 200) {
          setEstimatedReturnsError(false);
          setReturnYear(response.data.highest_return.year);
          setEstimatedReturns(response.data.highest_return.value);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setBtnLoading(false);
      });
  };

  //service to add basket details
  const _addBakset = () => {
    const is_post = true;
    const payload = {
      basket_style_id: null,
      image: fileUrl,
      name: basketName,
      description: basketDescription,
      order: Number(order),
      about_basket: answer,
      tag: selectedOptionGrowth,
      risk: curRisk.value,
      suitable_for: suitableFor,
      time_horizon: timeHorizon.value,
      funds: convertedFunds,
      return_year: returnYear,
      min_sip: Number(minSip),
      min_lumpsum: Number(lumpsum),
      return_percentage: Number(estimatedReturns),
      features: featureFields,
      basket_tag: basketTag,
      show_on_home_page: showOnHomePage,
    };
    APIService(is_post, "manage-basket/upsert", payload)
      .then((response) => {
        if (response.status_code === 200) {
          toast.success(response.message);
          setLoading(false);
          navigate("/manage-basket");
        } else if (response.status_code === 400) {
          if (response.data?.minimum_required_amount) {
            toast.error(
              `${response.message}  minimum amount:"${response.data.minimum_required_amount}"`,
            );
          } else {
            toast.error(response.message);
          }
        }
      })
      .catch((error) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  return loading ? (
    <div className="d-flex min-vh-100 justify-content-center align-items-center">
      <Loader width={30} />
    </div>
  ) : (
    <div className="row px-3 e-pt-20px">
      {/* Upload & Input Section */}
      <div className="col-12">
        <div className="row e-pt-20px e-primary-font-poppins">
          <div className="col-xl-8 col-lg-10 col-12">
            <div className="e-border-1px-pale-lavender e-border-radius-16 p-3 e-bg-ghost-white-dark">
              <div className="row">
                <div className="col-lg-10">
                  <h5 className="e-font-16 e-font-weight-600 e-text-eerie-black">
                    Add details
                  </h5>

                  <div className="row pt-3">
                    <Input
                      id="basket-name"
                      type="text"
                      label="Basket Name"
                      placeholder="Enter basket name"
                      require={true}
                      value={basketName}
                      error={linkError}
                      labelStyle="e-text-black e-primary-font-poppins e-font-weight-400 e-font-14 pb-2"
                      className="e-primary-font-poppins w-100"
                      onKeyPress={_addBanner}
                      handleChange={(e) => {
                        setLinkError(false);
                        setBasketName(e);
                      }}
                    />
                  </div>

                  <div className="row pt-3">
                    <Input
                      id="basket-description"
                      type="text"
                      label="Basket Description"
                      placeholder="Enter Basket Description"
                      require={true}
                      value={basketDescription}
                      autoFocus={false}
                      error={basketDescriptionError}
                      labelStyle="e-text-black e-primary-font-poppins e-font-weight-400 e-font-14 pb-2"
                      className="e-primary-font-poppins w-100"
                      handleChange={(e) => {
                        setBasketDescriptionError(false);
                        setBasketDescription(e);
                      }}
                    />
                  </div>

                  <div className="row pt-3">
                    <Input
                      id="basket-order"
                      type={"Number"}
                      label="Order"
                      placeholder="Enter order"
                      require={true}
                      value={order}
                      error={orderError}
                      labelStyle="e-text-black e-primary-font-poppins e-font-weight-400 e-font-14 pb-2"
                      className="e-primary-font-poppins w-100"
                      handleChange={(e) => {
                        setOrderError(false);
                        setOrder(e);
                      }}
                    />
                  </div>

                  <div className="row py-2 mt-1">
                    <div className="col">
                      <ToggleInput
                        type={2}
                        id="basket-tag"
                        label={"Basket Tag"}
                        checked={basketTag}
                        onChange={(val) => {
                          setBasketTag(val);
                          if (!val) setSelectedOptionGrowthError(false);
                        }}
                        labelStyle={
                          " e-primary-font-poppins e-font-14 e-font-weight-400 e-text-eerie-black"
                        }
                      />
                    </div>
                    {basketTag && (
                      <div>
                        <RadioGroup
                          require={true}
                          className="pt-3"
                          data={option}
                          selectedItem={selectedOptionGrowth}
                          onSelectItem={setSelectedOptionGrowth}
                          error={selectedOptionGrowthError}
                        />
                      </div>
                    )}
                  </div>

                  <div className="row py-2 mt-1">
                    <div className="col">
                      <ToggleInput
                        type={2}
                        id="show-on-home-page"
                        label={"Show on home page"}
                        checked={showOnHomePage}
                        onChange={setShowOnHomePage}
                        labelStyle={
                          " e-primary-font-poppins e-font-14 e-font-weight-400 e-text-eerie-black"
                        }
                      />
                    </div>
                  </div>

                  <div className="row  align-items-center">
                    <div className="d-flex justify-content-between">
                      <h5 className=" e-font-14 e-font-weight-400 e-text-black">
                        Features <span className="text-danger">*</span>
                      </h5>
                      <div className="d-flex align-items-center mb-2">
                        <img
                          src={
                            require("../../Assets/Image/ManageBasket/info.svg")
                              .default
                          }
                          alt="info icon"
                        />
                        <p className=" ps-2 e-font-12 m-0 e-font-weight-500 ">
                          You can add upto 3
                        </p>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="row row-cols-lg-3 row-cols-sm-2 row-cols-1 row-gap-2 ">
                        {featureFields.map((value, index) => (
                          <React.Fragment key={index}>
                            <div className="col position-relative">
                              <Input
                                require={true}
                                className="w-100 pe-lg-5"
                                type="text"
                                value={value}
                                error={featureErrors && featureErrors[index]}
                                handleChange={(val) => handleChange(index, val)}
                              />
                              {featureFields.length > 1 && (
                                <PrimaryButton
                                  size="14"
                                  ClassName="e-text-black e-close-marker bg-transparent p-0"
                                  onPress={() => handleRemove(index)}
                                  icon="close-button"
                                />
                              )}
                            </div>
                          </React.Fragment>
                        ))}
                        {featureFields.length < 3 && (
                          <div className="col-3">
                            <PrimaryButton
                              icon="plus"
                              name="Add new"
                              ClassName="e-text-blue-violet e-font-14 e-font-weight-500 bg-transparent px-0 pt-3"
                              onPress={handleAddNew}
                            />
                          </div>
                        )}
                      </div>
                      {featureErrors && featureErrors.some((e) => e) && (
                        <p className="mb-0 e-font-12 text-danger e-primary-font-poppins  pt-1">
                          {featureErrors?.length > 1
                            ? "Complete the feature name"
                            : "Add at least one feature"}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Risk Value */}
                  <div className="row">
                    <div className="col-12 py-2">
                      <h5 className="e-font-14 e-font-weight-400 e-text-black">
                        Risk Value <span className="text-danger">*</span>
                      </h5>
                      <div className="row">
                        <div className="col-12">
                          <CustomSelectBox
                            options={riskFilterData}
                            value={curRisk}
                            type={4}
                            selectChange={setCurRisk}
                            placeholder="Select Category"
                            error={riskError}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Suitability */}
                  <div className="row pt-2">
                    <Input
                      id="suitability"
                      type="text"
                      label="Suitability"
                      placeholder="Enter Suitability"
                      require={true}
                      value={suitableFor}
                      error={suitableForError}
                      labelStyle="e-text-black e-primary-font-poppins e-font-weight-400 e-font-14 pb-2"
                      className="e-primary-font-poppins w-100"
                      handleChange={(e) => {
                        setSuitableForError(false);
                        setSuitableFor(e);
                      }}
                    />
                  </div>
                  {/* Time Horizon */}
                  <div className="row pt-2">
                    <div className="col-12 py-2">
                      <h5 className="e-font-14 e-font-weight-400 e-text-black">
                        Time Horizon <span className="text-danger">*</span>
                      </h5>
                      <CustomSelectBox
                        options={timeHorizonData}
                        value={timeHorizon}
                        type={4}
                        selectChange={(val) => {
                          setTimeHorizon(val);
                          setTimeHorizonError(false);
                        }}
                        placeholder="Select Time Horizon"
                        error={timeHorizonError}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col">
                      <h5 className=" pt-3 pb-1 e-font-14 e-font-weight-400 e-primary-font-poppins e-text-eerie-black">
                        Upload Basket icon{" "}
                        <span className="e-font-14 e-font-weight-400 e-text-cg-red">
                          *
                        </span>
                      </h5>
                      <CustomFileUpload
                        file={file || fileUrl}
                        fileSize={3}
                        handleClear={() => {
                          setFile(null);
                          setFileUrl("");
                        }}
                        error={fileError}
                        handleChange={_handleImageUpload}
                        onUploadComplete={(url) => setFileUrl(url)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fund Management */}
            <div className="row pt-4">
              <h5 className=" e-font-16 e-primary-font-poppins e-font-weight-600 e-text-black pb-3">
                Composition
              </h5>
              <div className="d-flex justify-content-between">
                <h5 className=" e-font-14 e-font-weight-400 e-text-black">
                  Total Funds
                  <span className="e-font-weight-500 ps-1">
                    {fundsData?.length}
                  </span>
                </h5>
                <div className="d-flex align-items-center mb-2">
                  <img
                    src={
                      require("../../Assets/Image/ManageBasket/info.svg")
                        .default
                    }
                    alt="info icon"
                  />
                  <p className=" ps-2 e-font-12 m-0 e-font-weight-500 ">
                    Total allocation should be 100%
                  </p>
                </div>
              </div>
              <div className="col-sm-10 col-9">
                <CustomSearchInput
                  type="text"
                  id="search"
                  placeholder="Search with fund name"
                  className="w-100 e-search-input-padding"
                  value={searchValue}
                  handleChange={setSearchValue}
                  onKeyPress={() => {
                    _getFundsList();
                  }}
                />
              </div>
              <div className="col-sm-2 col-3 d-flex justify-content-end">
                <PrimaryButton
                  disabled={selectedOptions?.length === 0}
                  name="Add fund"
                  onPress={handleAddFunds}
                  ClassName="e-bg-blue-violet w-100 e-border-radius-pill text-white e-font-14 e-primary-font-poppins e-font-weight-500 py-2"
                />
              </div>
            </div>

            <div className="row pt-3">
              {searchValue.length === 0 ? (
                <div className="col-lg-12 d-flex flex-column row-gap-2">
                  {fundsData?.map((fund) => (
                    <div
                      key={fund.fund_id}
                      className="e-bg-magnolia e-border-radius-8 px-2"
                    >
                      <div className="row align-items-center justify-content-between">
                        <div className="col-md-1 col-2">
                          <img
                            src={fund.fund_image}
                            alt="bank"
                            className="img-fluid"
                          />
                        </div>
                        <div className="col-md-6 col-4">
                          <p className="e-font-14 e-font-weight-400 e-primary-font-poppins m-0">
                            {fund.fund_name}
                          </p>
                        </div>
                        <div className="col-2">
                          <div
                            className={`d-flex align-items-center justify-content-evenly py-2 px-4 e-border-radius-8 bg-white e-border-1px-silver ${
                              fundsAllocationError ? "e-border-danger" : ""
                            }`}
                          >
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step="0.01"
                              value={
                                fund.allocation_percentage === ""
                                  ? ""
                                  : fund.allocation_percentage
                              }
                              onChange={(e) =>
                                handleAllocationChange(
                                  fund.fund_id,
                                  e.target.value,
                                )
                              }
                              className="border-0 e-text-blue-violet e-font-weight-500 e-primary-font-poppins"
                              style={{
                                outline: "none",
                                width: "-webkit-fill-available",
                              }}
                            />
                            <div className="e-font-14 e-font-weight-500 e-primary-font-poppins e-text-blue-violet">
                              %
                            </div>
                          </div>
                        </div>
                        <div className="col-2 d-flex justify-content-end">
                          <PrimaryButton
                            name="Delete"
                            icon="delete"
                            onPress={() => {
                              handleDeleteFund(fund.fund_id);
                              setSelectedOptions((prev) =>
                                prev.filter((e) => e.fund_id !== fund.fund_id),
                              );
                            }}
                            ClassName="bg-transparent px-0 e-text-cg-red e-font-weight-400 e-font-14"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {fundsAllocationError && (
                    <div className="e-text-cg-red e-font-12">
                      {fundsAllocationError}
                    </div>
                  )}
                </div>
              ) : (
                <div className="row">
                  <div className="col-11">
                    <CustomSelectMenu
                      options={searchedFunds}
                      value={selectedOptions}
                      onChange={setSelectedOptions}
                      isMulti={true}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="row pt-3">
              <div className="col-12">
                <div className="e-border-1px-pale-lavender e-border-radius-16 p-3 e-bg-ghost-white-dark">
                  <h5 className="e-font-16 e-font-weight-500 e-text-eerie-black">
                    Min SIP & Lumpsum
                  </h5>
                  <div className="row row-cols-2">
                    <div className="col position-relative">
                      <Input
                        label="Min SIP"
                        require={true}
                        type={"Number"}
                        className="w-100 ps-4 py-3"
                        labelStyle={
                          "e-font-14 e-font-weight-400 e-primary-font-poppins pb-2"
                        }
                        value={minSip}
                        error={minSipError}
                        handleChange={(e) => {
                          setMinSip(e);
                          setMinSipError(false);
                        }}
                      />
                      <span className="e-money-marker top-50">₹</span>
                    </div>
                    <div className="col position-relative">
                      <Input
                        label="Min Lumpsum"
                        require={true}
                        labelStyle={
                          "e-font-14 e-font-weight-400 e-primary-font-poppins pb-2"
                        }
                        className="w-100 ps-4 py-3"
                        value={lumpsum}
                        type={"Number"}
                        error={lumpSumError}
                        handleChange={(e) => {
                          setLumpSum(e);
                          setLumpSumError(false);
                        }}
                      />
                      <span className="e-money-marker top-50">₹</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row pt-3">
              <div className="col-12">
                <div className="e-border-1px-pale-lavender e-border-radius-16 p-3 e-bg-ghost-white-dark">
                  <div className="row pt-3">
                    <h5 className=" e-font-16 e-font-weight-500 e-text-black">
                      Past Returns
                    </h5>
                    <div className="col-md-4 col-12 position-relative">
                      <Input
                        label={`Past Returns (${returnYear})`}
                        require={true}
                        labelStyle={
                          "e-font-14 e-font-weight-400 e-primary-font-poppins pb-2"
                        }
                        type={"Number"}
                        value={estimatedReturns}
                        error={estimatedReturnsError}
                        handleChange={(e) => {
                          setEstimatedReturns(e);
                          setEstimatedReturnsError(false);
                        }}
                        className="w-100 position-relative"
                      />
                      <span className=" e-text-black e-font-14 e-font-weight-400 e-percentage-marker top-50 pb-1">
                        %
                      </span>
                    </div>
                    <div className="col-md-8 col-12 d-flex justify-content-end pt-3">
                      <PrimaryButton
                        name={"Calculate Returns"}
                        ClassName="bg-transparent e-text-blue-violet e-font-weight-500 e-font-14"
                        onPress={_calculateReturns}
                        loading={btnloading}
                        disabled={selectedOptions.length === 0}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row pt-3">
              <div className="col-12">
                <div className="e-border-1px-pale-lavender e-border-radius-16 p-3 e-bg-ghost-white-dark">
                  <h5 className=" e-font-16 e-font-weight-600 e-text-black">
                    Past Returns
                  </h5>
                  <CustomTextArea
                    label="About Basket"
                    placeholder="Enter About basket"
                    labelStyle="e-font-14 e-font-weight-400 text-capitalize e-primary-font-poppins pb-2"
                    type="text"
                    require={true}
                    value={answer}
                    error={answerError}
                    className="w-100"
                    rows={6}
                    handleChange={(e) => {
                      setAnswerError(false);
                      setAnswer(e);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="row e-pt-36px">
              <div className="col-md-10 col-12">
                <div className="row">
                  <div className="col-6">
                    <PrimaryButton
                      ClassName="e-border-radius-pill e-border-1px-blue py-3 e-font-weight-500 e-font-16 w-100 bg-transparent e-text-blue-violet e-primary-font-poppins"
                      name="Cancel"
                      onPress={() => navigate("/manage-basket")}
                    />
                  </div>
                  <div className="col-6">
                    <PrimaryButton
                      ClassName="e-border-radius-pill e-font-weight-500 py-3 e-font-16 w-100 e-bg-blue-violet text-white e-primary-font-poppins"
                      name="Save"
                      onPress={_handleSave}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
